import { setOfferPageRooms, type OfferPageRoomsTransitionState } from 'contexts/OfferPage/offerPageRoomActions'
import { getOfferPageRoomsTransitionState } from 'selectors/offerPage/roomChangeSelectors'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { generateOfferPageRoom, OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import { validateSemiForm } from 'lib/forms/formUtils'
import { isBedbankOffer, isBundleOffer, isTourV2Offer } from 'lib/offer/offerTypes'
import {
  getBedbankAndFlightsChildMaxAge,
  getOfferAndFlightsMaxChildAge,
  getOfferMaxInfantAge,
  getTourV2OfferMinAdultAge,
  getTourV2OfferMinChildAge,
  getBundleAndSaveMaxChildAge,
  getBundleAndSaveMaxInfantAge,
  isSalesforceTourV2,
  getOfferMaxTeenagerAge,
} from 'lib/offer/offerUtils'
import { countOccupants, countOccupantsForBundleAndSave } from 'lib/offer/occupancyUtils'
import config from 'constants/config'
import { getTourFlightsEnabled } from 'selectors/getTourFlightsEnabled'
import { preventRenderWithClosedFilter, shouldCollectTourAdultAgesForFlights } from 'lib/offer/offerPageUtil'

interface Props {
  roomsTransitionState: OfferPageRoomsTransitionState
  rooms: Array<OfferPageRoom>
  offer: App.Offer | App.BedbankOffer | App.Tours.TourV2Offer
  tourVariation?: App.Tours.TourV2OfferVariation
  tourFlightsEnabled: boolean
  open?: boolean
  capacities?: Array<App.RoomCapacity>
  childrenAgeLabel?: string
  className?: string
  bundleOfferId?: string
  disableMultiRoom?: boolean
  maxRooms?: number
  editingQuantityDraft: boolean
  /** Disable all "+" buttons to prevent adding more occupants */
  disableAddOccupants?: boolean
  /** Maximum total occupants allowed (excluding infants). When set, new rooms will adjust adults to fit within limit */
  maxOccupants?: number
}

function OfferCapacityWithAgesSelection(props: Props) {
  const {
    rooms,
    roomsTransitionState,
    offer,
    tourVariation,
    tourFlightsEnabled,
    open,
    capacities,
    childrenAgeLabel: childrenAgeLabelProp,
    className,
    bundleOfferId,
    disableMultiRoom = !config.accommodation.allowsMultiRoom,
    maxRooms,
    editingQuantityDraft,
    disableAddOccupants,
    maxOccupants,
  } = props

  const intl = useIntl()
  const childrenAgeLabel = childrenAgeLabelProp ?? intl.formatMessage({
    id: 'EbrBVoqyE0WN72ptuE4q',
    defaultMessage: 'Age at time of check-in',
    description: 'Default label for children age selection in offer capacity picker',
  })

  const pickerRef = useRef<HTMLDivElement>(null)

  const dispatch = useOfferPageDispatch()

  useEffect(() => {
    if (pickerRef.current) {
      if (open) {
        validateSemiForm(pickerRef.current)
      }
    }
  }, [open])

  const childrenAgeThreshold = useMemo(() => {
    if (isBedbankOffer(offer)) {
      return getBedbankAndFlightsChildMaxAge(offer)
    } else if (isTourV2Offer(offer)) {
      return getTourV2OfferMinAdultAge(offer, tourVariation) - 1
    } else if (isBundleOffer(offer)) {
      return getBundleAndSaveMaxChildAge(offer)
    } else {
      return getOfferAndFlightsMaxChildAge(offer)
    }
  }, [offer, tourVariation])

  const isLETour = useMemo(() => (isTourV2Offer(offer) && isSalesforceTourV2(offer)), [offer])
  const tourMinChildAge = isTourV2Offer(offer) && !isLETour ? getTourV2OfferMinChildAge(offer, tourVariation) : undefined

  const infantAgeThreshold = useMemo(() => {
    if (isBundleOffer(offer)) {
      return getBundleAndSaveMaxInfantAge(offer)
    }
    return getOfferMaxInfantAge(offer)
  }, [offer])

  const teenagerAgeThreshold = useMemo(() => {
    return getOfferMaxTeenagerAge(offer)
  }, [offer])

  // Preserve the ages accepted by the picker, including flight children who are
  // adults under the hotel's pricing policy. Pricing categorises them separately.
  const onGuestChange = useCallback((nextRooms: Array<OfferPageRoom>) => {
    if (editingQuantityDraft) {
      const rooms = nextRooms.map(room => ({
        ...room,
        ...countOccupants([room], {
          maxChildAge: childrenAgeThreshold,
          maxInfantAge: infantAgeThreshold,
          maxTeenagerAge: teenagerAgeThreshold,
        }),
      }))
      dispatch({ type: OfferPageActions.setFlashQuantityDraftRooms, rooms })
      return
    }
    if (isBedbankOffer(offer) || isTourV2Offer(offer)) {
      dispatch(setOfferPageRooms(roomsTransitionState, nextRooms.map(room => generateOfferPageRoom(room))))
    } else {
      dispatch(setOfferPageRooms(roomsTransitionState, nextRooms.map((room) => ({
        ...room,
        ...(isBundleOffer(offer) ? countOccupantsForBundleAndSave([room], offer) : countOccupants([room], {
          maxChildAge: childrenAgeThreshold,
          maxInfantAge: infantAgeThreshold,
          maxTeenagerAge: teenagerAgeThreshold,
        })),
      }))))
    }
  }, [offer, dispatch, roomsTransitionState, childrenAgeThreshold, infantAgeThreshold, teenagerAgeThreshold, editingQuantityDraft])

  return (
    <OccupancyPicker
      ref={pickerRef}
      bundleOfferId={bundleOfferId}
      occupancies={rooms}
      saleUnit={offer.saleUnit}
      onChange={onGuestChange}
      maxChildAge={childrenAgeThreshold}
      minChildAge={tourMinChildAge}
      maxInfantAge={infantAgeThreshold}
      maxTeenagerAge={teenagerAgeThreshold}
      capacities={capacities}
      data-testid="offer-capacity-with-ages-selection-dropdown"
      className={className}
      childrenAgeLabel={childrenAgeLabel}
      enableMultiRoom={!disableMultiRoom}
      adultLabelType={isLETour ? 'traveller' : 'adult'}
      collectAdultAges={isTourV2Offer(offer) && shouldCollectTourAdultAgesForFlights(offer, tourFlightsEnabled, tourVariation)}
      childVariant={isLETour ? 'none' : undefined}
      offerType={offer.type}
      offerId={offer.id}
      maxRooms={maxRooms}
      disableAddOccupants={disableAddOccupants}
      maxOccupants={maxOccupants}
    />
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  bundleOfferId: state.bundleOffer?.id,
  rooms: state.flashQuantityDraft?.rooms ?? state.rooms,
  editingQuantityDraft: !!state.flashQuantityDraft,
  roomsTransitionState: getOfferPageRoomsTransitionState(state),
  offer: state.offer ?? state.bedbankOffer ?? state.tourV2Offer,
  tourVariation: state.tourV2Variation,
  tourFlightsEnabled: getTourFlightsEnabled(appState),
  open: state.activeFilter === 'capacity' || state.activeSubFilter === 'capacity' || state.activeHeaderFilter === 'capacity',
}), OfferCapacityWithAgesSelection, preventRenderWithClosedFilter)
